import { Reducer } from "redux";

import { Client, ClientContact, ClientCount } from "@/Contracts/Clients";
import { RecentOpportunityItemView } from "@/Contracts/Opportunity";
import { RecentDocumentItemView } from "@/Contracts/Documents";

import { ClientsActionTypes, ClientsActions } from "../../Actions/Clients";
import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata
} from "@/Contracts/UI";

export interface ClientsState {
	readonly isLoading: boolean;
	readonly clientList: Client[];
	readonly clientCount: ClientCount;
	readonly currentActiveClient: Client | null;
	readonly clientContactList: ClientContact[];
	readonly addClientDrawerOpen: boolean;
	readonly readonlyClientDrawerOpen: boolean;
	readonly editClientDrawerOpen: boolean;
	readonly addClientContactDrawerOpen: boolean;
	readonly editClientContactDrawerOpen: boolean;
	readonly viewList: CustomViewResponse | null;
	readonly shippingAddressDrawerOpen: boolean;
	readonly currentActiveView: CustomView | null;
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly isViewChanged: boolean;
	readonly isBulkActionsRecordsSelected: boolean;
	readonly recentOpportunityByClientId: Record<
		string,
		RecentOpportunityItemView | null
	>;
	readonly recentDocumentByClientId: Record<
		string,
		RecentDocumentItemView | null
	>;
}

export const initialClientState: ClientsState = {
	isLoading: false,
	clientList: new Array<Client>(),
	clientCount: new ClientCount(),
	currentActiveClient: null,
	clientContactList: new Array<ClientContact>(),
	addClientDrawerOpen: false,
	readonlyClientDrawerOpen: false,
	editClientDrawerOpen: false,
	addClientContactDrawerOpen: false,
	editClientContactDrawerOpen: false,
	shippingAddressDrawerOpen: false,
	viewList: null,
	currentActiveView: null,
	viewMetadata: null,
	isViewChanged: false,
	isBulkActionsRecordsSelected: false,
	recentOpportunityByClientId: {},
	recentDocumentByClientId: {}
};

export const ClientsReducer: Reducer<ClientsState, ClientsActions> = (
	state: ClientsState = initialClientState,
	action: ClientsActions
): ClientsState => {
	switch (action.type) {
		case ClientsActionTypes.SHOW_CLIENTS_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case ClientsActionTypes.HIDE_CLIENTS_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case ClientsActionTypes.CLIENTS_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.CLIENTS_SUCCESSFUL: {
			return {
				...state,
				clientList: action.clientList,
				clientCount: action.clientCount
			};
		}
		case ClientsActionTypes.CLIENTS_FAILED: {
			return state;
		}
		case ClientsActionTypes.CLIENTS_RESET: {
			return {
				...state,
				clientList: new Array<Client>(),
				clientCount: new ClientCount(),
				recentOpportunityByClientId: {},
				recentDocumentByClientId: {}
			};
		}
		case ClientsActionTypes.CLIENT_DETAILS_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.CLIENT_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveClient: action.currentActiveClient
			};
		}
		case ClientsActionTypes.CLIENT_DETAILS_FAILED: {
			return state;
		}
		case ClientsActionTypes.CLIENT_DETAILS_UPDATED: {
			return {
				...state,
				currentActiveClient: action.currentActiveClient
			};
		}
		case ClientsActionTypes.CLIENT_DETAILS_RESET: {
			return {
				...state,
				currentActiveClient: null
			};
		}
		case ClientsActionTypes.CLIENT_CONTACTS_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.CLIENT_CONTACTS_SUCCESSFUL: {
			return {
				...state,
				clientContactList: action.clientContactList
			};
		}
		case ClientsActionTypes.CLIENT_CONTACTS_FAILED: {
			return state;
		}
		case ClientsActionTypes.CLIENT_CONTACTS_RESET: {
			return {
				...state,
				clientContactList: new Array<ClientContact>()
			};
		}
		case ClientsActionTypes.DELETE_CLIENT_CONTACT_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.DELETE_CLIENT_CONTACT_SUCCESSFUL: {
			return {
				...state,
				clientContactList: state.clientContactList.filter(
					clientContact =>
						clientContact.id !== action.deleteClientContact.id
				)
			};
		}
		case ClientsActionTypes.DELETE_CLIENT_CONTACT_FAILED: {
			return state;
		}
		case ClientsActionTypes.ADD_CLIENT_CONTACT_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.ADD_CLIENT_CONTACT_SUCCESSFUL: {
			return {
				...state,
				clientContactList: action.updatedClientContactList
			};
		}
		case ClientsActionTypes.ADD_CLIENT_CONTACT_FAILED: {
			return state;
		}
		case ClientsActionTypes.EDIT_CLIENT_CONTACT_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.EDIT_CLIENT_CONTACT_SUCCESSFUL: {
			return {
				...state,
				clientContactList: action.updatedClientContactList
			};
		}
		case ClientsActionTypes.EDIT_CLIENT_CONTACT_FAILED: {
			return state;
		}
		case ClientsActionTypes.DELETE_CLIENT_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.DELETE_CLIENT_SUCCESSFUL: {
			return {
				...state,
				clientList: state.clientList.filter(
					client => client.id !== action.deleteClient.id
				)
			};
		}
		case ClientsActionTypes.DELETE_CLIENT_FAILED: {
			return state;
		}
		case ClientsActionTypes.ADD_CLIENT_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.ADD_CLIENT_SUCCESSFUL: {
			return {
				...state,
				clientList: [...state.clientList, action.addClient]
			};
		}
		case ClientsActionTypes.ADD_CLIENT_FAILED: {
			return state;
		}
		case ClientsActionTypes.EDIT_CLIENT_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.EDIT_CLIENT_SUCCESSFUL: {
			const clientList = state.clientList.filter(
				client => client.id !== action.editClient.id
			);
			const updateClientIndex = state.clientList.findIndex(
				client => client.id === action.editClient.id
			);
			if (updateClientIndex > -1) {
				clientList.splice(updateClientIndex, 0, action.editClient);
			}
			return {
				...state,
				clientList
			};
		}
		case ClientsActionTypes.EDIT_CLIENT_FAILED: {
			return state;
		}
		case ClientsActionTypes.ADD_CLIENT_DRAWER_OPENED: {
			return {
				...state,
				addClientDrawerOpen: true
			};
		}
		case ClientsActionTypes.ADD_CLIENT_DRAWER_CLOSED: {
			return {
				...state,
				addClientDrawerOpen: false
			};
		}
		case ClientsActionTypes.EDIT_CLIENT_DRAWER_OPENED: {
			return {
				...state,
				editClientDrawerOpen: true
			};
		}
		case ClientsActionTypes.EDIT_CLIENT_DRAWER_CLOSED: {
			return {
				...state,
				editClientDrawerOpen: false
			};
		}
		case ClientsActionTypes.ADD_CLIENT_CONTACT_DRAWER_OPENED: {
			return {
				...state,
				addClientContactDrawerOpen: true
			};
		}
		case ClientsActionTypes.ADD_CLIENT_CONTACT_DRAWER_CLOSED: {
			return {
				...state,
				addClientContactDrawerOpen: false
			};
		}
		case ClientsActionTypes.EDIT_CLIENT_CONTACT_DRAWER_OPENED: {
			return {
				...state,
				editClientContactDrawerOpen: true
			};
		}
		case ClientsActionTypes.EDIT_CLIENT_CONTACT_DRAWER_CLOSED: {
			return {
				...state,
				editClientContactDrawerOpen: false
			};
		}
		case ClientsActionTypes.MAKE_PRIMARY_CONTACT_SUCCESSFUL: {
			const clientContactList = action.updatedClientContactList;
			return {
				...state,
				clientContactList
			};
		}
		case ClientsActionTypes.READONLY_CLIENT_DRAWER_OPENED: {
			return {
				...state,
				readonlyClientDrawerOpen: true,
				currentActiveClient: action.clientData
			};
		}
		case ClientsActionTypes.READONLY_CLIENT_DRAWER_CLOSED: {
			return {
				...state,
				readonlyClientDrawerOpen: false
			};
		}
		case ClientsActionTypes.MAKE_PRIMARY_CONTACT_FAILED: {
			return state;
		}
		case ClientsActionTypes.UPDATE_CLIENT_OPPORTUNITY: {
			return {
				...state,
				currentActiveClient: state.currentActiveClient
					? {
							...state.currentActiveClient,
							opportunities: action.opportunities
					  }
					: null
			};
		}
		case ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_SUCCESSFUL: {
			return {
				...state,
				viewMetadata: action.metadata
			};
		}
		case ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_FAILED: {
			return state;
		}
		case ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_SUCCESSFUL: {
			return {
				...state,
				viewList: action.viewList
			};
		}
		case ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_FAILED: {
			return state;
		}
		case ClientsActionTypes.FETCH_CLIENT_LIST_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.FETCH_CLIENT_LIST_SUCCESSFUL: {
			return {
				...state,
				clientList: action.clientList.data,
				clientCount: action.clientList.filtered_count_map
			};
		}
		case ClientsActionTypes.FETCH_CLIENT_LIST_FAILED: {
			return state;
		}
		case ClientsActionTypes.CREATE_CLIENT_VIEW_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.CREATE_CLIENT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case ClientsActionTypes.CREATE_CLIENT_VIEW_FAILED: {
			return state;
		}
		case ClientsActionTypes.UPDATE_CLIENT_VIEW_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.UPDATE_CLIENT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case ClientsActionTypes.UPDATE_CLIENT_VIEW_FAILED: {
			return state;
		}
		case ClientsActionTypes.DELETE_CLIENT_VIEW_REQUESTED: {
			return state;
		}
		case ClientsActionTypes.DELETE_CLIENT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case ClientsActionTypes.DELETE_CLIENT_VIEW_FAILED: {
			return state;
		}
		case ClientsActionTypes.UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};
		}
		case ClientsActionTypes.RESET_CLIENTS_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: null,
				isViewChanged: false,
				viewList: null,
				viewMetadata: null,
				recentOpportunityByClientId: {},
				recentDocumentByClientId: {}
			};
		}
		case ClientsActionTypes.BULK_ACTION_RECORDS_SELECTED: {
			return {
				...state,
				isBulkActionsRecordsSelected: true
			};
		}
		case ClientsActionTypes.BULK_ACTION_RECORDS_RESET: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case ClientsActionTypes.BULK_DELETE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case ClientsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case ClientsActionTypes.BULK_DELETE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case ClientsActionTypes.BULK_UPDATE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case ClientsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case ClientsActionTypes.BULK_UPDATE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case ClientsActionTypes.SHIPPING_ADDRESS_DRAWER_OPENED: {
			return {
				...state,
				shippingAddressDrawerOpen: true
			};
		}
		case ClientsActionTypes.SHIPPING_ADDRESS_DRAWER_CLOSED: {
			return {
				...state,
				shippingAddressDrawerOpen: false
			};
		}
		case ClientsActionTypes.MERGE_RECENT_OPPORTUNITY_FOR_CLIENTS: {
			return {
				...state,
				recentOpportunityByClientId: {
					...state.recentOpportunityByClientId,
					...action.map
				}
			};
		}
		case ClientsActionTypes.MERGE_RECENT_DOCUMENT_FOR_CLIENTS: {
			return {
				...state,
				recentDocumentByClientId: {
					...state.recentDocumentByClientId,
					...action.map
				}
			};
		}
		default: {
			return state;
		}
	}
};

export default ClientsReducer;
